import React, { useEffect, useRef } from "react";
import { Link } from "react-router";
import { LgPhrase } from "@/components/luagate-ui/LgPhrase";
import {
  BookOpen,
  BookMarked,
  GraduationCap,
  FileText,
  PlayCircle,
  Cpu,
  Lock,
  Search,
  Loader2,
} from "lucide-react";
import { useSearchSession } from "@/hooks/useSearchSession";
import type { SearchEntryType } from "@/hooks/useSearch";
import { PUBLIC_SEARCH_GROUP_ORDER } from "@/lib/search/search-session";
import { SearchModal } from "../SearchModal/SearchModal";

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  isTrial?: boolean;
}

type GroupMeta = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
};

const GROUP_META: Record<SearchEntryType, GroupMeta> = {
  cs: {
    label: "コンピュータサイエンス",
    icon: Cpu,
    colorClass: "text-indigo-500",
  },
  chapter: {
    label: "実践プロジェクト",
    icon: BookOpen,
    colorClass: "text-primary",
  },
  glossary: {
    label: "用語辞書",
    icon: BookMarked,
    colorClass: "text-blue-500",
  },
  lesson: {
    label: "学習パス",
    icon: GraduationCap,
    colorClass: "text-green-500",
  },
  textbook: {
    label: "教科書",
    icon: FileText,
    colorClass: "text-amber-500",
  },
  video: {
    label: "授業アーカイブ",
    icon: PlayCircle,
    colorClass: "text-rose-500",
  },
};

/** 打つ前 (空の窓) に出す、誰でも開ける人気の入口。既に検索結果が出ている間は出さない */
const POPULAR_ENTRIES: readonly { label: string; to: string }[] = [
  { label: "コンピュータサイエンス", to: "/computer-science" },
  { label: "IT資格", to: "/certification" },
  { label: "面接対策", to: "/interview-questions" },
];

/**
 * 検索キーワードに合致する箇所を <mark> で示す。
 * 地を塗る印 (背景色の札) は、題が折り返す行で 1 語の当たりが 2 つの箱に割れて見える (「コン」「ピュータ」)。
 * 太字 + 下線だけにすると、行が折り返しても字の続きのまま読めて軽い (/luagate の探す窓 lg-find.css と同じ考え)
 */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim() || !text) return <>{text}</>;
  const trimmed = query.trim();
  // エスケープして正規表現で大文字小文字無視マッチ
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(re);
  const lower = trimmed.toLowerCase();
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === lower ? (
          <mark
            key={i}
            className="bg-transparent text-primary font-bold underline decoration-2 underline-offset-2"
          >
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

export function GlobalSearch({ isOpen, onClose, isTrial = false }: GlobalSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    query,
    setQuery,
    orderedGroups,
    items: flatItems,
    selectedIndex,
    searching: isLoading,
    error,
    onKeyDown: handleKeyDown,
    go: handleNavigate,
  } = useSearchSession({ isOpen, isTrial, onClose, groupOrder: PUBLIC_SEARCH_GROUP_ORDER });

  // モーダル open 時に input フォーカス
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // 描画後にフォーカスするため次フレーム
      const id = requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen]);

  const totalCount = flatItems.length;

  return (
    <SearchModal isOpen={isOpen} onClose={onClose} className="sm:max-w-2xl bg-surface rounded-2xl border border-main">
      <div className="p-4 bg-main rounded-2xl">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sub" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="章・用語・カリキュラム・記事を検索..."
            className="w-full pl-10 pr-10 py-3 bg-second border border-main text-main placeholder:text-sub rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="w-5 h-5 text-sub animate-spin" />
            </div>
          )}
        </div>

        {/* 打つ前の案内。人気の入口と、使える人にだけ操作のヒント */}
        {!query && (
          <div className="mt-4 px-3 py-2">
            <div className="flex flex-wrap gap-2">
              {POPULAR_ENTRIES.map((entry) => (
                <Link
                  key={entry.to}
                  to={entry.to}
                  onClick={onClose}
                  className="rounded-full border border-main bg-second px-3 py-1.5 text-sm text-main hover:bg-third"
                >
                  {entry.label}
                </Link>
              ))}
            </div>
            <div className="mt-3 hidden items-center gap-2 text-sm text-sub sm:[@media(hover:hover)_and_(pointer:fine)]:flex">
              <kbd className="px-2 py-1 bg-second border border-main rounded text-xs font-mono text-main">
                ↑↓
              </kbd>
              <span>選択</span>
              <kbd className="px-2 py-1 bg-second border border-main rounded text-xs font-mono text-main">
                Enter
              </kbd>
              <span>決定</span>
              <kbd className="px-2 py-1 bg-second border border-main rounded text-xs font-mono text-main">
                Esc
              </kbd>
              <span>閉じる</span>
            </div>
          </div>
        )}

        {/* Results */}
        {query && totalCount > 0 && (
          <div className="mt-4 max-h-[60vh] overflow-y-auto space-y-5">
            {orderedGroups.map((group) => {
              const meta = GROUP_META[group.type];
              // 口が知らない種類を返しても、窓ごと落とさない
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <div key={group.type}>
                  <div className="flex items-center gap-2 mb-2 px-2">
                    <Icon className={`w-4 h-4 ${meta.colorClass}`} />
                    <h4 className="text-sm font-semibold text-main">
                      {meta.label}
                    </h4>
                    <span className="text-xs text-sub">
                      ({group.items.length}件)
                    </span>
                  </div>
                  <div className="space-y-1">
                    {group.items.map((item) => {
                      const globalIndex = flatItems.indexOf(item);
                      const locked = isTrial && item.isTrialVisible === false;
                      const isSelected = globalIndex === selectedIndex;
                      return (
                        <button
                          key={`${item.type}-${item.id}`}
                          onClick={() => handleNavigate(item)}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-colors border ${
                            isSelected
                              ? "bg-primary/10 border-primary/30"
                              : "border-transparent hover:bg-second"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              {/* 組の見出しが種類を言うので、行の札は種類を繰り返さず、行どうしを見分ける字 (学習パスのコース名、CS の道名など) だけを
                                  題の上の独立した行に置く。題と同じ行に並べると、題の長さ次第で札が下に折れたり並んだりして行ごとに高さが揃わない */}
                              {item.category && (
                                <p className="text-xs text-sub truncate">{item.category}</p>
                              )}
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-base font-bold text-main truncate">
                                  <HighlightedText text={item.title} query={query} />
                                </h3>
                                {locked && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-second border border-main text-sub">
                                    <Lock className="w-3 h-3" />
                                    有料メンバー限定
                                  </span>
                                )}
                              </div>
                              {item.excerpt && (
                                <p className="mt-1 text-sm text-sub line-clamp-2">
                                  <LgPhrase><HighlightedText text={item.excerpt} query={query} /></LgPhrase>
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-second border border-main rounded-xl">
            <p className="text-sm text-main">
              検索中にエラーが発生しました。しばらく待ってから再度お試しください。
            </p>
          </div>
        )}

        {/* No Results */}
        {query && !isLoading && !error && totalCount === 0 && (
          <div className="mt-8 text-center py-8">
            <Search className="w-12 h-12 text-sub mx-auto mb-3 opacity-50" />
            <p className="text-main font-medium">該当なし</p>
            <p className="mt-1 text-sm text-sub">
              言葉を変えて検索してみてください
            </p>
          </div>
        )}

        {/* Loading State (初回・空状態でのロード) */}
        {query && isLoading && totalCount === 0 && !error && (
          <div className="mt-8 text-center py-8">
            <Loader2 className="w-8 h-8 text-sub mx-auto mb-3 animate-spin" />
            <p className="text-sm text-sub">検索中...</p>
          </div>
        )}
      </div>
    </SearchModal>
  );
}
